/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { useEffect, useReducer } from "react";
import useAgent from "./useAgent";

// Action types
const actionTypes = {
	ON_CONNECTED: "ON_CONNECTED",
	SET_CONCURRENT_CAPACITY: "SET_CONCURRENT_CAPACITY",
	SET_AGENT_STATUS: "SET_AGENT_STATUS",
	SET_AGENT_AWAY_ON_CONNECT: "SET_AGENT_AWAY_ON_CONNECT",
};

// Initial state
const initialState = {
	// configuredCapacity is the max capacity of an agent.
	configuredCapacity: {
		VOICE: 0,
		CHAT: 0,
		TASK: 0,
	},
	currentContacts: [],
	acwContacts: new Set(),
	initContacts: new Set(),
	agentStateOnConnect: {
		name: "",
		type: "",
		isAgentAway: false,
	},
};

// Reducer
const reducer = (state, action) => {
	switch (action.type) {
		case actionTypes.ON_CONNECTED: {
			let contacts = action.value;
			const stateClone = { ...state };
			const currentContacts = [];
			contacts.forEach((contact) => {
				const newContact = {
					contactId: contact.contactId,
					type: contact.getType().toUpperCase(),
					status: contact.getStatus().type,
					state: contact.getState().type,
				};
				currentContacts.push(newContact);
			});
			stateClone.currentContacts = currentContacts;
			return stateClone;
		}
		case actionTypes.SET_CONCURRENT_CAPACITY: {
			const stateClone = { ...state };
			stateClone.configuredCapacity = action.value;
			return stateClone;
		}
		case actionTypes.SET_AGENT_STATUS: {
			const stateClone = { ...state };
			stateClone.agentStateOnConnect = action.value;
			return stateClone;
		}
		default:
			throw new Error("Invalid input");
	}
};

const useAgentStats = () => {
	const [agentStats, dispatch] = useReducer(reducer, initialState);
	const currentAgent = useAgent();

	const { configuredCapacity, currentContacts } = agentStats;
	const currentCapacity = currentContacts.reduce(
		(prevVal, contact) => {
			prevVal[contact.type]++;
			return prevVal;
		},
		{ TASK: 0, CHAT: 0, VOICE: 0 },
	);
	agentStats.currentCapacity = currentCapacity;
	const isAtMaxCapacity =
		(configuredCapacity.TASK !== 0 && configuredCapacity.TASK === currentCapacity.TASK) ||
		(configuredCapacity.CHAT !== 0 && configuredCapacity.CHAT === currentCapacity.CHAT) ||
		(configuredCapacity.VOICE !== 0 && configuredCapacity.VOICE === currentCapacity.VOICE);

	useEffect(() => {
		if (!currentAgent) {
			return;
		}
		const agentContacts = currentAgent.getContacts();
		dispatch({ type: actionTypes.ON_CONNECTED, value: agentContacts });

		// Fetch configuration of the agent when page gets loaded.
		var config = currentAgent.getConfiguration();
		const { channelConcurrencyMap } = config.routingProfile;
		const configuredCapacity = {
			VOICE: channelConcurrencyMap.VOICE || 0,
			CHAT: channelConcurrencyMap.CHAT || 0,
			TASK: channelConcurrencyMap.TASK || 0,
		};
		dispatch({
			type: actionTypes.SET_CONCURRENT_CAPACITY,
			value: configuredCapacity,
		});
		var agentState = currentAgent.getState();
		dispatch({ type: actionTypes.SET_AGENT_STATUS, value: agentState });
		currentAgent.onStateChange(function () {
			var agentState = currentAgent.getState();
			dispatch({ type: actionTypes.SET_AGENT_STATUS, value: agentState });
		});
	}, [currentAgent]);

	useEffect(() => {
		const events = window.connect.core.getEventBus();
		const eventSubscriptions = [];
		const getAgentContacts = () => {
			if (!currentAgent) {
				return;
			}
			let contacts = currentAgent.getContacts();
			dispatch({ type: actionTypes.ON_CONNECTED, value: contacts });
		};

		const ContactEvents = window.connect.ContactEvents;

		Object.keys(ContactEvents).forEach((event) => {
			eventSubscriptions.push(events.subscribe(ContactEvents[event], getAgentContacts));
		});

		return () => {
			if (eventSubscriptions.length === 0) {
				return;
			}
			eventSubscriptions.forEach((listener) => listener.unsubscribe());
		};
	}, [currentAgent]);

	return { agentStats, isAtMaxCapacity };
};

export default useAgentStats;
